import {
  Show,
  SimpleShowLayout,
  TextField,
  BooleanField,
  DateField,
  FunctionField,
  TopToolbar,
  EditButton,
  useTranslate,
  useRecordContext,
} from 'react-admin';
import { Box, Chip, Typography } from '@mui/material';
import { formatJson } from './playbookFormHelpers';

const ShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
);

// Pretty JSON in a monospace box (read-only). Empty value renders a muted placeholder.
const JsonBlock = ({ value }: { value: unknown }) => {
  const translate = useTranslate();
  const text = formatJson(value);
  if (!text) {
    return (
      <Typography component="span" color="text.disabled">
        {translate('resources.ai-playbooks.show.emptyJson')}
      </Typography>
    );
  }
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        backgroundColor: '#F5F5F5',
        borderRadius: 1,
        fontFamily: 'monospace',
        fontSize: '0.8rem',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Box>
  );
};

// A simple comma-joined chip list for the two string arrays.
const StringArrayChips = ({ values }: { values: unknown }) => {
  const translate = useTranslate();
  const arr = Array.isArray(values) ? (values as string[]) : [];
  if (arr.length === 0) {
    return (
      <Typography component="span" color="text.disabled">
        {translate('resources.ai-playbooks.show.emptyArray')}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {arr.map((v, i) => (
        <Chip key={`${v}-${i}`} size="small" label={v} />
      ))}
    </Box>
  );
};

// The source investigation provenance. Sub-plan 14's detail is a dialog (no standalone deep-link
// route), so the id is rendered as text. TODO(sub-plan 14): link to a dedicated investigation
// detail route if/when one is added.
const SourceInvestigation = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const id = record?.createdFromInvestigationId as string | null | undefined;
  if (!id) {
    return (
      <Typography component="span" color="text.disabled">
        {translate('resources.ai-playbooks.show.manualOrigin')}
      </Typography>
    );
  }
  return <Typography component="span">{id}</Typography>;
};

const CategoryValue = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const category = (record?.rootCauseCategory as string | null) ?? null;
  if (!category) {
    return (
      <Typography component="span" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Chip
      size="small"
      label={translate(`resources.ai-playbooks.categories.${category}`, { _: category })}
    />
  );
};

// Read-only full record view.
export const PlaybookShow = () => {
  const translate = useTranslate();
  return (
    <Show actions={<ShowActions />}>
      <SimpleShowLayout>
        <TextField source="name" label={translate('resources.ai-playbooks.fields.name')} />
        <TextField
          source="description"
          label={translate('resources.ai-playbooks.fields.description')}
          emptyText="—"
        />
        <FunctionField
          label={translate('resources.ai-playbooks.fields.rootCauseCategory')}
          render={() => <CategoryValue />}
        />
        <BooleanField source="isActive" label={translate('resources.ai-playbooks.fields.isActive')} />
        <FunctionField
          label={translate('resources.ai-playbooks.fields.symptomKeywords')}
          render={(record: any) => <StringArrayChips values={record.symptomKeywords} />}
        />
        <FunctionField
          label={translate('resources.ai-playbooks.fields.symptomExamples')}
          render={(record: any) => <StringArrayChips values={record.symptomExamples} />}
        />
        <TextField
          source="remediationGuidance"
          label={translate('resources.ai-playbooks.fields.remediationGuidance')}
          emptyText="—"
        />
        <FunctionField
          label={translate('resources.ai-playbooks.fields.diagnosticSteps')}
          render={(record: any) => <JsonBlock value={record.diagnosticSteps} />}
        />
        <FunctionField
          label={translate('resources.ai-playbooks.fields.triggerConditions')}
          render={(record: any) => <JsonBlock value={record.triggerConditions} />}
        />
        <FunctionField
          label={translate('resources.ai-playbooks.fields.createdFromInvestigationId')}
          render={() => <SourceInvestigation />}
        />
        <DateField
          source="createdAt"
          showTime
          label={translate('resources.ai-playbooks.fields.createdAt')}
        />
        <DateField
          source="updatedAt"
          showTime
          label={translate('resources.ai-playbooks.fields.updatedAt')}
        />
      </SimpleShowLayout>
    </Show>
  );
};

export default PlaybookShow;
